/* eslint-disable no-console */
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { catalogItemSlice } from 'src/entities/catalog';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import { CatalogItemViewModel } from '@common/model/catalog/item/view';
import { coreCatalogItemApi } from '@cp/entities/catalogItem';
import { EntityId } from '@reduxjs/toolkit';
import { AsyncResult, Result } from '@youhelp-ru/core';
import { logError, resetLoading, toError } from '../lib/result';
import { useCatalogItemStorageService } from './useCatalogItemStorageService';

const namespace = 'OpenCatalogItem';

export interface OpenCatalogItemParams {
  numberToAccessOfItems: number;
}

export type CatalogItemFeature = (
  catalogItemId: number,
  params?: Partial<OpenCatalogItemParams>,
) => AsyncResult<CatalogItemViewModel, Error>;

const defaultParams: OpenCatalogItemParams = {
  numberToAccessOfItems: 4,
};

export const useOpenCatalogItem = (): [CatalogItemFeature, boolean] => {
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  const isLoggedIn = useIsLoggedIn();
  const storage = useCatalogItemStorageService();
  const [queryCatalogItem] = coreCatalogItemApi.endpoints.getById.useLazyQuery();

  const processRunner = useCallback<CatalogItemFeature>(
    async (catalogItemId, params) => {
      const { numberToAccessOfItems } = { ...defaultParams, ...params };
      console.debug(`[${namespace}]: Starting the proccess with ${catalogItemId}...`);
      setIsLoading(true);

      const catalogItemResult = await Result.fromAsync(() => queryCatalogItem({ id: catalogItemId }).unwrap(), toError);

      return catalogItemResult
        .flatMap((catalogItem) => {
          const Err = Result.Err<typeof catalogItem, Error>;
          const accessOfItems = storage.getAccessOfItems<EntityId[]>(catalogItem.type);
          const hasToLargeAccessOfItems = accessOfItems.length >= numberToAccessOfItems && !accessOfItems.includes(catalogItemId);

          if (hasToLargeAccessOfItems && !isLoggedIn) {
            return Err(new Error('The number of available entities has been exceeded'));
          }

          return Result.Ok(catalogItem);
        })
        .tap((catalogItem) => {
          if (!isLoggedIn) {
            storage.addAccessOfItem(catalogItemId, catalogItem.type);
          }

          console.debug(`[${namespace}]: The process was completed successfully!`);
          dispatch(catalogItemSlice.actions.setCatalogItem(catalogItem));
        })
        .tap(resetLoading(setIsLoading))
        .tapError(resetLoading(setIsLoading))
        .tapError(logError(namespace));
    },
    [queryCatalogItem, storage, isLoggedIn, dispatch],
  );

  return [processRunner, isLoading];
};
